import { useEffect, useState } from 'react';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Link } from 'react-router-dom';
import {
  Building,
  Briefcase,
  Download,
  FolderKanban,
  Plus,
  Search,
  Sparkles,
  Upload,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useClients,
  useCreateClient,
  useSeedClients,
  useBulkDeleteClients,
} from '../hooks/useClients';
import { usePermissions } from '../hooks/usePermissions';
import { useI18n } from '../contexts/I18nContext';
import type { ClientFilters, ClientInput, ClientSortField } from '../lib/clientsApi';
import { SortHeader, sortHeaderProps } from '../components/ui/SortHeader';
import { downloadDirectoryExcel } from '../lib/directoryExcelApi';
import { formatCurrency } from '../lib/format';
import { FadeIn, SkeletonTable } from '../components/ui/Skeleton';
import { Select } from '../components/ui/Select';
import { useClientIndustries } from '../hooks/useActors';
import { MobileFilterSheet, type FilterDescriptor } from '../components/ui/MobileFilterSheet';
import { ClientFormModal } from '../components/clients/ClientFormModal';
import { ExcelImportDialog } from '../components/directory/ExcelImportDialog';
import { BulkDeleteBar } from '../components/directory/BulkDeleteBar';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useToast } from '../components/tasks/linear/Toast';

// Delete is a soft delete (isActive=false) — default to active-only so deleted
// rows actually leave the list (same fix as suppliers/subscriptions).
const DEFAULT_FILTERS: ClientFilters = { sort: 'name', page: 1, limit: 20, active: 'true' };

export function ClientsListPage() {
  const [filters, setFilters] = useState<ClientFilters>(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);

  // Bulk selection (rows on the current page only; cleared on filter/page change).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading, isError } = useClients(filters);
  const clients = data?.data ?? [];
  const meta = data?.meta;

  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useI18n();
  const createMutation = useCreateClient();
  const seedMutation = useSeedClients();
  const bulkDeleteMutation = useBulkDeleteClients();
  const { can } = usePermissions();
  const canCreateClients = can('clients', 'canCreate');
  const canViewClients = can('clients', 'canView');
  const canDeleteClients = can('clients', 'canDelete');

  async function handleExport() {
    setExporting(true);
    try {
      await downloadDirectoryExcel('clients');
    } catch {
      toast.show(t('clients.exportError'));
    } finally {
      setExporting(false);
    }
  }

  function patchFilters(patch: Partial<ClientFilters>) {
    setSelected(new Set());
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  }

  // Selection helpers: only rows visible on the current page can be selected.
  const allVisibleSelected = clients.length > 0 && clients.every((c) => selected.has(c.id));

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelected(allVisibleSelected ? new Set() : new Set(clients.map((c) => c.id)));
  }

  function goToPage(page: number) {
    setSelected(new Set());
    setFilters((p) => ({ ...p, page }));
  }

  function confirmBulkDelete() {
    bulkDeleteMutation.mutate([...selected], {
      onSuccess: () => {
        setConfirmOpen(false);
        setSelected(new Set());
      },
    });
  }

  function setSort(token: ClientFilters['sort']) {
    setSelected(new Set());
    setFilters((prev) => ({ ...prev, sort: token, page: 1 }));
  }

  function header(field: ClientSortField) {
    return sortHeaderProps(filters.sort ?? 'name', field, setSort);
  }

  // Search as the user types (debounced); Enter still searches right away.
  const debouncedSearch = useDebouncedValue(searchInput.trim());
  useEffect(() => {
    if (debouncedSearch !== (filters.search ?? '')) {
      patchFilters({ search: debouncedSearch || undefined });
    }
    // Only a settled keystroke triggers this, not every filter change.
  }, [debouncedSearch]);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    patchFilters({ search: searchInput.trim() || undefined });
  }

  async function handleCreate(input: ClientInput) {
    await createMutation.mutateAsync(input);
    setShowCreate(false);
  }

  async function handleSeed() {
    setSeedMsg(null);
    const result = await seedMutation.mutateAsync();
    setSeedMsg(
      `${result.created} clienți creați · ${result.linkedInvoices + result.linkedSmartbill} facturi · ${result.linkedProjects} proiecte asociate.`,
    );
  }

  const { data: industries } = useClientIndustries();
  const industryOptions = (industries ?? []).map((i) => ({ value: i.id, label: i.name }));

  const mobileFilters: FilterDescriptor[] = [
    {
      kind: 'select',
      id: 'active',
      label: t('common.active'),
      icon: <Building size={16} />,
      value: filters.active ?? '',
      onChange: (v) => patchFilters({ active: (v || undefined) as ClientFilters['active'] }),
      options: [
        { value: 'true', label: t('common.active') },
        { value: 'false', label: t('common.inactive') },
      ],
      placeholder: t('common.all'),
    },
    {
      kind: 'select',
      id: 'projects',
      label: t('clients.col.projects'),
      icon: <FolderKanban size={16} />,
      value: filters.hasProjects ?? '',
      onChange: (v) =>
        patchFilters({ hasProjects: (v || undefined) as ClientFilters['hasProjects'] }),
      options: [
        { value: 'true', label: t('clients.filter.withProjects') },
        { value: 'false', label: t('clients.filter.withoutProjects') },
      ],
      placeholder: t('clients.filter.allProjects'),
    },
    {
      kind: 'select',
      id: 'industry',
      label: t('clients.industry'),
      icon: <Briefcase size={16} />,
      value: filters.industryId ?? '',
      onChange: (v) => patchFilters({ industryId: v || undefined }),
      options: industryOptions,
      placeholder: t('clients.filter.allIndustries'),
    },
  ];

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <Building className="text-brand-600" size={24} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('clients.title')}</h1>
            <p className="text-sm text-gray-500">{t('clients.subtitle')}</p>
          </div>
        </div>
        {/* Below lg the actions are a grid of equal halves (the primary one
            spans the row when the count is odd), not a ragged wrap. */}
        <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-wrap lg:items-center [&>*:last-child:nth-child(odd)]:col-span-2">
          {canViewClients && (
            <button
              type="button"
              onClick={() => void handleExport()}
              disabled={exporting}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Download size={16} />
              {exporting ? t('common.exporting') : t('common.exportExcel')}
            </button>
          )}
          {canCreateClients && (
            <button
              type="button"
              onClick={() => setShowImport(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Upload size={16} />
              {t('common.importExcel')}
            </button>
          )}
          {canCreateClients && (
            <button
              type="button"
              onClick={handleSeed}
              disabled={seedMutation.isPending}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Sparkles size={16} />
              {seedMutation.isPending ? t('clients.seeding') : t('clients.seedFromInvoices')}
            </button>
          )}
          {canCreateClients && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <Plus size={16} />
              {t('clients.new')}
            </button>
          )}
        </div>
      </div>

      {seedMsg && (
        <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          {seedMsg}
        </div>
      )}

      {/* Below lg (phones, and tablets where the sidebar eats half the
          width): search + one Filtre button (bottom sheet), like Leaduri,
          instead of the stacked fields. */}
      <div className="mt-4 flex items-center gap-2 lg:hidden">
        <form onSubmit={submitSearch} className="relative min-w-0 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('clients.search')}
            aria-label={t('common.search')}
            className="ui-input min-h-[44px] pl-9"
          />
        </form>
        <MobileFilterSheet
          filters={mobileFilters}
          onClearAll={() =>
            patchFilters({ active: undefined, hasProjects: undefined, industryId: undefined })
          }
        />
      </div>

      {/* Filters */}
      <div className="mt-6 hidden gap-3 lg:flex lg:items-end">
        <form onSubmit={submitSearch} className="flex-1">
          <label className="ui-field-label">{t('common.search')}</label>
          <div className="relative">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('clients.search')}
              className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </form>
        <Select
          label={t('common.active')}
          clearable
          value={filters.active ?? ''}
          onChange={(v) => patchFilters({ active: (v || undefined) as ClientFilters['active'] })}
          placeholder={t('common.all')}
          options={[
            { value: 'true', label: t('common.active') },
            { value: 'false', label: t('common.inactive') },
          ]}
          className="w-full sm:w-40"
        />
        <Select
          label={t('clients.col.projects')}
          clearable
          value={filters.hasProjects ?? ''}
          onChange={(v) =>
            patchFilters({ hasProjects: (v || undefined) as ClientFilters['hasProjects'] })
          }
          placeholder={t('clients.filter.allProjects')}
          options={[
            { value: 'true', label: t('clients.filter.withProjects') },
            { value: 'false', label: t('clients.filter.withoutProjects') },
          ]}
          className="w-full sm:w-44"
        />
        <Select
          label={t('clients.industry')}
          clearable
          searchable
          value={filters.industryId ?? ''}
          onChange={(v) => patchFilters({ industryId: v || undefined })}
          placeholder={t('clients.filter.allIndustries')}
          options={industryOptions}
          className="w-full sm:w-48"
        />
      </div>

      {/* Table */}
      <div className="mt-6">
        {isLoading && <SkeletonTable rows={8} columns={7} />}
        {isError && <p className="text-red-600">{t('clients.loadError')}</p>}
        {!isLoading && !isError && clients.length === 0 && (
          <p className="py-12 text-center text-gray-500">{t('clients.empty')}</p>
        )}
        {!isLoading && !isError && clients.length > 0 && (
          <FadeIn>
            {/* Below lg: one card per row instead of a table that only
                scrolled sideways; the name leads, the details follow. */}
            <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white lg:hidden">
              {clients.map((c) => (
                <li key={c.id} className="flex items-start gap-3 px-3 py-3">
                  {canDeleteClients && (
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggleRow(c.id)}
                      aria-label={c.name}
                      className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <Link
                        to={`/clients/${c.id}`}
                        className="min-w-0 truncate font-medium text-brand-700 hover:underline"
                      >
                        {c.name}
                      </Link>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                          c.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {c.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-gray-500">
                      {[c.industry?.name, c.contactPerson, c.cifCui].filter(Boolean).join(' · ') ||
                        '—'}
                    </div>
                    <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <dt className="text-gray-500">{t('clients.col.invoiced')}</dt>
                        <dd className="tabular-nums text-gray-900">
                          {formatCurrency(c.totalInvoiced, c.currency)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t('clients.col.outstanding')}</dt>
                        <dd
                          className={`tabular-nums ${c.outstanding > 0 ? 'text-amber-700' : 'text-gray-900'}`}
                        >
                          {formatCurrency(c.outstanding, c.currency)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t('clients.col.projects')}</dt>
                        <dd className="tabular-nums text-gray-900">{c.projectCount}</dd>
                      </div>
                    </dl>
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto rounded-lg border border-gray-200 lg:block">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {canDeleteClients && (
                      <th className="w-10 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleAllVisible}
                          aria-label={t('bulk.selected', { count: selected.size })}
                          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        />
                      </th>
                    )}
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label={t('clients.col.client')} {...header('name')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label={t('clients.col.cui')} {...header('cifCui')} />
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">
                      <SortHeader label={t('clients.col.projects')} {...header('projectCount')} />
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">
                      <SortHeader label={t('clients.col.invoiced')} {...header('totalInvoiced')} />
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">
                      <SortHeader label={t('clients.col.received')} {...header('totalReceived')} />
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">
                      <SortHeader label={t('clients.col.outstanding')} {...header('outstanding')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label={t('common.status')} {...header('isActive')} />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {clients.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      {canDeleteClients && (
                        <td className="w-10 px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selected.has(c.id)}
                            onChange={() => toggleRow(c.id)}
                            aria-label={c.name}
                            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <Link
                          to={`/clients/${c.id}`}
                          className="font-medium text-brand-700 hover:underline"
                        >
                          {c.name}
                        </Link>
                        {(c.industry || c.contactPerson) && (
                          <div className="text-xs text-gray-500">
                            {[c.industry?.name, c.contactPerson].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{c.cifCui ?? '—'}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{c.projectCount}</td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        {formatCurrency(c.totalInvoiced, c.currency)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                        {c.totalReceived ? (
                          formatCurrency(c.totalReceived, c.totalReceivedCurrency)
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        <span className={c.outstanding > 0 ? 'text-amber-700' : 'text-gray-500'}>
                          {formatCurrency(c.outstanding, c.currency)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            c.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {c.isActive ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {meta && meta.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span>
                  {t('clients.pagination', {
                    page: meta.page,
                    totalPages: meta.totalPages,
                    total: meta.total,
                  })}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={meta.page <= 1}
                    onClick={() => goToPage((filters.page ?? 1) - 1)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('common.previous')}
                  </button>
                  <button
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => goToPage((filters.page ?? 1) + 1)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </FadeIn>
        )}
      </div>

      {canDeleteClients && (
        <BulkDeleteBar
          count={selected.size}
          onDelete={() => setConfirmOpen(true)}
          onClear={() => setSelected(new Set())}
          error={bulkDeleteMutation.isError && !confirmOpen ? t('bulk.error') : undefined}
        />
      )}

      {confirmOpen && (
        <ConfirmDialog
          danger
          title={t('clients.bulk.confirmTitle')}
          message={t('clients.bulk.confirmMessage', { count: selected.size })}
          confirmLabel={t('bulk.delete')}
          busy={bulkDeleteMutation.isPending}
          error={bulkDeleteMutation.isError ? t('bulk.error') : undefined}
          onConfirm={confirmBulkDelete}
          onCancel={() => setConfirmOpen(false)}
        />
      )}

      {showCreate && (
        <ClientFormModal
          title={t('clients.new')}
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
          submitting={createMutation.isPending}
        />
      )}

      {showImport && (
        <ExcelImportDialog
          entityType="clients"
          onClose={() => setShowImport(false)}
          onImportComplete={() => {
            void queryClient.invalidateQueries({ queryKey: ['clients'] });
          }}
        />
      )}
    </div>
  );
}
